<template>
	<Col :span="24" :offset="0">
		<TypographyTitle class="pd-vertical" :level="3">{{ $t('account.linkedTelegrams.header') }}</TypographyTitle>
		<div class="scrollable-x">
			<Table :columns="columns" :data-source="accounts" :pagination="false" :loading="isLoading">
				<template #headerCell="{ column }">
					<template v-if="column.dataIndex === 'errorEnabled'">
						<AlertLevelDisplay level="ERROR" />
					</template>
					<template v-if="column.dataIndex === 'warningEnabled'">
						<AlertLevelDisplay level="WARNING" />
					</template>
					<template v-if="column.dataIndex === 'infoEnabled'">
						<AlertLevelDisplay level="INFO" />
					</template>
					<template v-if="column.dataIndex === 'debugEnabled'">
						<AlertLevelDisplay level="DEBUG" />
					</template>
				</template>
				<template #bodyCell="{ column, record }">
					<template v-if="column.dataIndex === 'telegramUsername'">
						<Space>
							{{ record.telegramUsername }}
							<Tooltip
								v-if="record.errorsCount >= ALERT_ERRORS_COUNT"
								:trigger="['hover']"
								placement="top"
								:title="$t('account.linkedTelegrams.errorsTooltipText')"
							>
								<WarningTwoTone :two-tone-color="PINK" />
							</Tooltip>
							<Button
								v-if="!record.verified"
								type="primary"
								size="small"
								@click="startVerification(record)"
							>
								{{ $t('account.linkedTelegrams.verifyButtonLabel') }}
							</Button>
						</Space>
					</template>
					<template v-else-if="column.dataIndex === 'alertsEnabled'">
						<Switch
							v-model:checked="record.alertsEnabled"
							size="small"
							:disabled="!record.verified"
							@change="accountUpdate(record)"
						/>
					</template>
					<template v-else-if="column.dataIndex === 'errorEnabled'">
						<Switch
							v-if="record.verified"
							v-model:checked="record.errorEnabled"
							:disabled="!record.alertsEnabled"
							size="small"
							@change="accountUpdate(record)"
						/>
					</template>
					<template v-else-if="column.dataIndex === 'warningEnabled'">
						<Switch
							v-if="record.verified"
							v-model:checked="record.warningEnabled"
							:disabled="!record.alertsEnabled"
							size="small"
							@change="accountUpdate(record)"
						/>
					</template>
					<template v-else-if="column.dataIndex === 'infoEnabled'">
						<Switch
							v-if="record.verified"
							v-model:checked="record.infoEnabled"
							:disabled="!record.alertsEnabled"
							size="small"
							@change="accountUpdate(record)"
						/>
					</template>
					<template v-else-if="column.dataIndex === 'debugEnabled'">
						<Switch
							v-if="record.verified"
							v-model:checked="record.debugEnabled"
							:disabled="!record.alertsEnabled"
							size="small"
							@change="accountUpdate(record)"
						/>
					</template>
					<template v-else-if="column.dataIndex === 'delete'">
						<PopconfirmButton
							:title="$t('account.linkedTelegrams.deleteConfirmation')"
							:ok-text="$t('account.linkedTelegrams.deleteButtonLabel')"
							:cancel-text="$t('account.linkedTelegrams.cancelButtonLabel')"
							size="small"
							:onConfirm="() => accountDelete(record)"
						>
							{{ $t('account.linkedTelegrams.deleteButtonLabel') }}
						</PopconfirmButton>
					</template>
					<template v-else-if="column.dataIndex === 'test'">
						<Button
							v-if="record.verified"
							:disabled="!record.alertsEnabled"
							size="small"
							@click="accountTest(record)"
						>
							{{ $t('account.linkedTelegrams.testButtonLabel') }}
						</Button>
					</template>
				</template>
			</Table>
		</div>
		<Space class="pd-vertical">
			<Form
				layout="inline"
				:model="formState"
			>
				<FormItem
					v-bind="validateInfosNewTelegram"
				>
					<InputRestricted
						v-model="formState.newTelegram"
						:style="{ width: '300px' }"
						:maxlength="MAX_LENGTH"
						:placeholder="$t('account.linkedTelegrams.newTelegramLabel')"
						:allowedFormatChecker="alphaNumericChecker"
						:toolTipText="$t('account.linkedTelegrams.toolTipText')"
						@keyup.enter="onAccountAdd"
					/>
				</FormItem>
				<FormItem>
					<Button type="primary" @click.prevent="onAccountAdd">{{ $t('account.linkedTelegrams.accountAddButtonLabel') }}</Button>
				</FormItem>
			</Form>
		</Space>
	</Col>
	<TelegramVerificationModal
		v-model:account="verifyModalAccount"
		:botName="botName"
		@updateAccounts="updateAccounts"
	/>
</template>

<script>
import { ref, reactive, onMounted, computed, watch } from 'vue';
import {
	Col, Button, TypographyTitle,
	Table, Switch, Form, FormItem, Space, Tooltip, message,
} from 'ant-design-vue';
import { WarningTwoTone } from '@ant-design/icons-vue';

const useForm = Form.useForm;

import { useI18n } from 'vue-i18n'

import Api from '@/api/Api';
import { toCamelCaseList } from '@/api/ApiHelpers';
import Consts from '@/api/Consts';
import Validators from '@/api/Validators';
import Utils from '@/api/Utils';

import { showErrorMessage } from '@/components/common/errorMessage';

import InputRestricted from '@/components/common/InputRestricted';
import AlertLevelDisplay from '@/components/common/AlertLevelDisplay';
import TelegramVerificationModal from '@/components/account/TelegramVerificationModal';
import PopconfirmButton from '@/components/common/PopconfirmButton.vue';


export default {
	components: {
		Col, Button, TypographyTitle,
		Table, Switch, Form, FormItem, Space, Tooltip,
		InputRestricted, TelegramVerificationModal, AlertLevelDisplay, WarningTwoTone, PopconfirmButton,
	},
	setup() {
		const { t } = useI18n();
		const isLoading = ref(false);
		const validated = ref(false);
		const accounts = ref([]);
		const lastNewTelegram = ref(null);
		const formState = reactive({
			newTelegram: '',
		});
		const validateNewTelegram = async (_rule, value) => {
			if (value.length < 5) {
				return Promise.reject(t('account.linkedTelegrams.newTelegramHelp'));
			}
			return Promise.resolve();
		};
		const rulesRef = reactive({
			newTelegram: [{ validator: validateNewTelegram }],
		});
		const { validate, validateInfos } = useForm(formState, rulesRef);

		const botName = ref('');

		const verifyModalAccount = ref(null);
		const startVerification = account => {
			lastNewTelegram.value = null;
			verifyModalAccount.value = account;
		}

		const updateAccounts = () => {
			isLoading.value = true;
			Api.alertsTgGetList(data => {
				accounts.value = toCamelCaseList(data.accounts);
				botName.value = data.bot_name;
				isLoading.value = false;
				if (lastNewTelegram.value != null) {
					// если updateAccounts выполняется после ввода имени нового акка, сразу открываем попап верификации
					startVerification(accounts.value.filter(account => account.telegramUsername === lastNewTelegram.value)[0]);
				}
			});
		};
		onMounted(updateAccounts);

		const simpleHandler = (successMessage, successHandler) => {
			const handler = response => {
				if (response.status === 'ok') {
					message.success(t('account.linkedTelegrams.' + successMessage));
					if (successHandler != null) {
						successHandler();
					}
				} else {
					showErrorMessage(response.errors);
				}
				isLoading.value = false;
				updateAccounts();
			}
			return handler;
		}

		const accountUpdateHandler = response => {
			if (response.status !== 'ok') {
				showErrorMessage(response.errors);
			}
		};

		const accountUpdate = record => {
			Api.alertsTgUpdate(record, accountUpdateHandler);
		};

		const accountDelete = record => {
			isLoading.value = true;
			Api.alertsTgDelete(record.telegramUsername, simpleHandler('accountDeleted'));
		};

		const accountTest = record => {
			Api.alertsTgTest(record.telegramUsername, simpleHandler('accountTestSent'));
		};

		const onAccountAdd = () => {
			validated.value = true;
			validate().then(() => {
				isLoading.value = true;
				Api.alertsTgCreate(formState.newTelegram, simpleHandler('newTelegramAdded', () => {
					lastNewTelegram.value = Utils.toObject(formState).newTelegram;
					formState.newTelegram = '';
				}));
			}).catch(() => {
				isLoading.value = false;
			});
		};
		const validateInfosNewTelegram = computed(() => {
			return validated.value ? validateInfos.newTelegram : null;
		})
		watch(
			() => formState.newTelegram,
			() => { validated.value = false },
		);

		return {
			isLoading,
			accounts,
			formState,
			validateInfos,
			validateInfosNewTelegram,
			columns: Consts.linkedTelegramsTableColumnsComp,
			MAX_LENGTH: 32,
			alphaNumericChecker: Validators.alphaNumericChecker,

			accountUpdate,
			accountDelete,
			accountTest,

			ALERT_ERRORS_COUNT: 5,
			PINK: Consts.COLORS.pink,

			botName,
			verifyModalAccount,
			startVerification,

			onAccountAdd,
			updateAccounts,
		};
	},
}
</script>
